import type { HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import type BBoxObject from './BBoxObject';
import { BBOX_COLOR, RESIZE_HANDLE_DEBUG_COLORS } from './common';

const DEBUG_MODE: boolean = false;
export const RADIUS = 10;

export interface ScaleState {
  prevPoint: Point;
}

interface ResizeHandleOptions {
  point: Point;
  index: number;
  bbox: BBoxObject;
}

export default class ResizeHandle extends CanvasObject {
  lngLat: LngLat;
  point: Point;
  index: number;
  bbox: BBoxObject;
  radius: number = RADIUS;

  scaleState: ScaleState = {
    prevPoint: { x: NaN, y: NaN },
  };

  constructor(id: string, surface: SurfaceBase, options: ResizeHandleOptions) {
    super(id, surface);
    this.interactive = true;
    this.point = options.point;
    this.bbox = options.bbox;
    this.index = options.index;
    this.lngLat = this.surface.toMapCoords(this.point);

    this.on('hoverStart', (e) => this.onHoverStart(e));
    this.on('hoverEnd', () => this.onHoverEnd());
  }

  onHoverStart = (e: HoverEvent) => {
    this.radius = 2 * RADIUS;
    this.renderAll();
    const c = this.bbox.parent.getCenter();
    const angle = Math.round(geometry.radiansToDegrees(geometry.lineAngle(c, e.point)));
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 18 18" xml:space="preserve" version="1.1" xmlns:xlink="http://www.w3.org/1999/xlink" transform="rotate(${angle})"><path d="M4.562 4.438 0 9l4.562 4.562v-3.053h8.877v3.053L18 9 13.438 4.438v3.053H4.562V4.438z"></path></svg>`;
    var encodedData = window.btoa(svg);
    this.surface.setCursor(`url('data:image/svg+xml;base64,${encodedData}') 12 12, auto`);
  };

  onHoverEnd = () => {
    this.radius = RADIUS;
    this.renderAll();
    this.surface.resetCursor();
  };

  setPoint = (point: Point) => {
    this.point = point;
  };

  setLngLat = (lngLat: LngLat) => {
    this.lngLat = lngLat;
  };

  refreshScreenCoords(): void {
    this.point = this.surface.toScreenCoords(this.lngLat);
    super.refreshScreenCoords();
  }

  refreshMapCoords(): void {
    this.lngLat = this.surface.toMapCoords(this.point);
  }

  getLngLatAsArray = () => {
    return [this.lngLat.lng, this.lngLat.lat];
  };

  render(
    ctx: CanvasRenderingContext2D,
    strokeColor?: string | undefined,
    strokeWidth?: number | undefined,
    isDashed?: boolean | undefined,
    strokeOpacity?: number | undefined,
    isBorderHidden?: boolean | undefined,
  ): void {
    const color = DEBUG_MODE ? RESIZE_HANDLE_DEBUG_COLORS[this.index] : BBOX_COLOR;
    const r = this.radius;
    const p = this.point;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.fillRect(p.x - r / 2, p.y - r / 2, r, r);
    ctx.restore();
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    const r = this.radius * 2;
    const p = this.point;

    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.fillRect(p.x - r / 2, p.y - r / 2, r, r);
    ctx.restore();
  }
}
